import React from 'react';
import {inject, observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {HistoryConsoleMainTab, IHistoryConsoleController} from './controller';
import {HistoryConsoleTopBar} from './components/topbar/HistoryConsoleTopBar';
import {TsDataTable, DEFAULT_PAGE_SIZE} from '../../components/ts-data-table';
import {defaultSessionsColumns} from './sessions-columns';
import {ExpandedRow} from './components/ExpandedRow';
import {NoResults} from './components/no-results/NoResults';
import {LiveSession} from './icons';

import './style.scss';
import Timeline from './components/timeline/Timeline';
import {SimpleTabs} from '@techsee/techsee-ui-common/lib/tabs/simple';
import {RightBar} from './components/right-bar/RightBar';
import {Compare} from './components/compare/Compare';

interface HistoryConsoleProps {
    historyConsoleController?: IHistoryConsoleController;
}

const mapStoresToProps = (stores: any) => ({
    historyConsoleController: stores.historyConsoleStore?.historyConsoleController
});

@inject(mapStoresToProps)
@observer
export class HistoryConsole extends TechseeBaseComponent<HistoryConsoleProps> {
    componentWillUnmount() {
        const {historyConsoleController} = this.props;

        if (historyConsoleController) {
            historyConsoleController.dispose();
        }
    }

    renderInternal(): JSX.Element {
        const {historyConsoleController} = this.props;

        if (!historyConsoleController) {
            return <div className='history-console' />;
        }

        const translate = historyConsoleController.translate;
        const {state} = historyConsoleController;
        const hasSessions = state.sessionsResults.length > 0;
        const tabsInitialIndex = hasSessions ? state.mainTabIndex : HistoryConsoleMainTab.Sessions;
        const tabsList = [
            {
                title: translate('REACT.HISTORY_CONSOLE.SESSIONS'),
                content: () => (
                    <div className='history-console-grid-wrap'>
                        {state.sessionsResults.length === 0 && !state.isLoading ? (
                            <NoResults translate={translate} />
                        ) : (
                            <TsDataTable
                                data={state.sessionsResults.map((item) => ({value: item}))}
                                columns={defaultSessionsColumns}
                                totalRows={state.totalResults}
                                translate={translate}
                                pageIndex={state.pageIndex}
                                pageSize={DEFAULT_PAGE_SIZE}
                                isLoading={state.isLoading}
                                manualPagination
                                onPageChange={(p) => historyConsoleController.onPageChange(p)}
                                initialSorting={
                                    state.sortField ? {field: state.sortField, desc: state.sortDesc} : undefined
                                }
                                onSortingChange={(s) => historyConsoleController.onSortingChange(s)}
                                expandedRowIndex={state.historyConsoleSessionsIndexSelected}
                                getRowCanExpand={() => true}
                                renderSubComponent={({row}) => {
                                    const sessionId = row.value.sessionId;

                                    return (
                                        <ExpandedRow
                                            sessionId={sessionId}
                                            scansMap={state.expandedSessionScans}
                                            loadingMap={state.expandedSessionLoading}
                                            noScansLabel={translate('REACT.HISTORY_CONSOLE.NO_SCANS')}
                                            onItemClick={(index) =>
                                                historyConsoleController.onExpandedScanItemClick(sessionId, index)
                                            }
                                            onFetchScans={() => historyConsoleController.fetchSessionScans(sessionId)}
                                        />
                                    );
                                }}
                            />
                        )}
                    </div>
                )
            },
            {
                title: translate('REACT.HISTORY_CONSOLE.COMPARE'),
                disabled: state.sessionsResults.length === 0,
                content: () => (
                    <div className={'history-console-compare-container'}>
                        <Compare controller={historyConsoleController.compareController} />
                    </div>
                )
            }
        ];

        return (
            <div className={'history-console-container'}>
                <div className='history-console use-common-styles dashboard-main-container'>
                    <div>
                        <div className='history-console-top-bar-wrap'>
                            <HistoryConsoleTopBar ctrl={historyConsoleController} />
                        </div>
                        <div className='storyline-header-row'>
                            <h1 className={'storyline-header'}>{translate('REACT.HISTORY_CONSOLE.STORYLINE')}</h1>
                            {historyConsoleController.showStartLiveSession && (
                                <button
                                    className='start-live-session-btn'
                                    type='button'
                                    title={translate('REACT.HISTORY_CONSOLE.START_LIVE_SESSION')}
                                    onClick={() => historyConsoleController.startLiveSession()}>
                                    <span>{translate('REACT.HISTORY_CONSOLE.START_LIVE_SESSION')}</span>
                                    <span className='start-live-session-icon'>{LiveSession}</span>
                                </button>
                            )}
                        </div>
                        <div className={'sessions-timeline-container'}>
                            <p className={'sessions-timeline-info'}>{translate('REACT.HISTORY_CONSOLE.TIMELINE')}</p>
                            {historyConsoleController.state.sessionsTimeLineData.length > 0 && (
                                <Timeline
                                    data={historyConsoleController.state.sessionsTimeLineData}
                                    onItemClick={historyConsoleController.onSetHistoryConsoleSessionsIndexSelected}
                                    selectedIndex={state.historyConsoleSessionsIndexSelected}
                                />
                            )}
                        </div>
                        <nav className='history-console-tabs-nav'>
                            <SimpleTabs
                                key={hasSessions ? 'history-console-tabs-sessions' : 'history-console-tabs-empty'}
                                uid='history-console-tabs'
                                tabsList={tabsList}
                                initialTabIndex={tabsInitialIndex}
                                onChange={(index: number) => historyConsoleController.onHistoryConsoleTabChange(index)}
                            />
                        </nav>
                    </div>
                </div>
                <RightBar controller={historyConsoleController} />
            </div>
        );
    }
}
